'use client'

import { analytics } from '@/lib/analytics'
import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import {
  useSessionAwareConnectedIntegrationsQuery,
  useSessionAwareOnboardingQuery,
} from '@/lib/features/user/userApi'
import { initiateShopifyOAuth } from '@/lib/shopify-oauth-auth'
import { connectWordPressOAuthIntegration } from '@/lib/wordpress-oauth-auth'
import { Eye, EyeOff } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Modal from '../Modal/Modal'
import { Button } from '../ui/button'
import { Spinner } from '../ui/spinner'
import { ShopifyCredentialsForm } from './ShopifyCredentialsForm'
import { WordPressCredentialsForm } from './WordPressCredentialsForm'
import { WordPressSeoSetupModal } from './WordPressSeoSetupModal'

type CredentialField = {
  key: string
  label: string
  type: 'text' | 'password' | 'checkbox'
  placeholder?: string
  description?: string
}

/**
 * Configuration for non-OAuth integrations.
 * Each integration defines the credential fields needed for authentication.
 */
const INTEGRATION_CONFIG: Record<
  'wordpress' | 'framer' | 'shopify',
  {
    name: string
    fields: CredentialField[]
  }
> = {
  wordpress: {
    name: 'WordPress',
    fields: [
      {
        key: 'siteUrl',
        label: 'Site URL',
        type: 'text',
        placeholder: 'https://yoursite.com',
        description: 'Your WordPress site URL',
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'admin',
        description: 'Your WordPress username',
      },
      {
        key: 'applicationPassword',
        label: 'Application Password',
        type: 'password',
        placeholder: 'xxxx xxxx xxxx xxxx xxxx xxxx',
        description: 'Create this in WordPress under Users → Profile → Application Passwords',
      },
      {
        key: 'selfHosted',
        label: 'Self-Hosted WordPress',
        type: 'checkbox',
        description: 'Check this if your site is self-hosted (not WordPress.com)',
      },
    ],
  },
  framer: {
    name: 'Framer',
    fields: [
      {
        key: 'connectionUrl',
        label: 'Connection URL',
        type: 'text',
        placeholder: 'https://mcp.unframer.co/sse?id=...&secret=...',
        description: 'The URL uses Server-Sent Events (SSE) for communication',
      },
    ],
  },
  shopify: {
    name: 'Shopify',
    fields: [
      {
        key: 'shopDomain',
        label: 'Shop Domain',
        type: 'text',
        placeholder: 'mystore.myshopify.com',
        description: 'Your Shopify store domain (e.g., mystore.myshopify.com)',
      },
    ],
  },
}

type ApiCredentialsModalProps = {
  source?: 'chat' | 'menu' // Where the integration is being connected from
  isOpen: boolean
  appSlug: 'wordpress' | 'framer' | 'shopify'
  editMode?: boolean
  onClose: () => void
  onSuccess: (id: string, credentials: Record<string, string>) => void
}

export const ApiCredentialsModal = memo(function ApiCredentialsModal({
  isOpen,
  appSlug,
  editMode = false,
  onClose,
  onSuccess,
  source = 'menu', // Default to menu for backward compatibility
}: ApiCredentialsModalProps) {
  const currentBusinessProfileId = useCurrentBusinessProfileId()
  const { data: connectedIntegrations } = useSessionAwareConnectedIntegrationsQuery(
    currentBusinessProfileId || undefined
  )
  const { data: onboardingData } = useSessionAwareOnboardingQuery()
  const [state, setState] = useState({
    credentials: {} as Record<string, string>,
    isLoading: false,
    isTesting: false,
    isOAuthInProgress: false,
    showSeoModal: false,
    isLoadingCredentials: false,
  })
  const [showFramerUrl, setShowFramerUrl] = useState(false)

  const {
    credentials,
    isLoading,
    isTesting,
    isOAuthInProgress,
    showSeoModal,
    isLoadingCredentials,
  } = state

  const config = INTEGRATION_CONFIG[appSlug]

  // Check if selfHosted is checked (WordPress only)
  const isSelfHosted = appSlug === 'wordpress' && credentials['selfHosted'] === 'true'
  const isFramer = appSlug === 'framer'
  const isShopify = appSlug === 'shopify'

  // Load existing credentials in edit mode
  useEffect(() => {
    if (editMode && isOpen) {
      setState((prev) => ({ ...prev, isLoadingCredentials: true }))

      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }

      fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/${appSlug}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )
        .then((response) => response.json())
        .then((result) => {
          if (result.success && result.credentials) {
            setState((prev) => ({
              ...prev,
              credentials: result.credentials,
              isLoadingCredentials: false,
            }))
          } else {
            toast.error('Failed to load credentials')
            setState((prev) => ({ ...prev, isLoadingCredentials: false }))
          }
        })
        .catch((error) => {
          console.error('Error loading credentials:', error)
          toast.error('Failed to load credentials')
          setState((prev) => ({ ...prev, isLoadingCredentials: false }))
        })
    }
  }, [editMode, isOpen, appSlug, currentBusinessProfileId])

  const handleFieldChange = useCallback((key: string, value: string) => {
    setState((prev) => ({
      ...prev,
      credentials: { ...prev.credentials, [key]: value },
    }))
  }, [])

  const handleTest = useCallback(async () => {
    setState((prev) => ({ ...prev, isTesting: true }))
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/${appSlug}/test${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(credentials),
        }
      )

      const result = await response.json()

      if (response.ok && result.success) {
        toast.success(`Successfully connected to ${config.name}!`)
      } else {
        toast.error(result.error || `Failed to connect to ${config.name}`)
      }
    } catch (error) {
      console.error('Error testing credentials:', error)
      toast.error('Failed to test connection')
    } finally {
      setState((prev) => ({ ...prev, isTesting: false }))
    }
  }, [appSlug, config.name, credentials, currentBusinessProfileId])

  const handleConnect = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }))
    try {
      // Test connection first for non-Framer integrations before saving
      if (!isFramer) {
        const searchParams = new URLSearchParams()
        if (currentBusinessProfileId) {
          searchParams.set('businessProfileId', currentBusinessProfileId)
        }

        const testResponse = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/${appSlug}/test${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
          }
        )

        const testResult = await testResponse.json()

        if (!testResponse.ok || !testResult.success) {
          toast.error(testResult.error || `Failed to connect to ${config.name}`)
          setState((prev) => ({ ...prev, isLoading: false }))
          return
        }
      }

      // For WordPress self-hosted, show SEO setup modal before saving
      if (appSlug === 'wordpress' && isSelfHosted) {
        setState((prev) => ({ ...prev, isLoading: false, showSeoModal: true }))
        return
      }

      // Save credentials to database (ephemeral for Framer)
      const saveUrl = new URL(
        `/v2/integrations/credentials/${appSlug}`,
        process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI
      )
      if (currentBusinessProfileId) {
        saveUrl.searchParams.set('businessProfileId', currentBusinessProfileId)
      }
      if (isFramer) {
        saveUrl.searchParams.set('ephemeral', 'true')
      }

      const saveResponse = await fetch(saveUrl.toString(), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      })

      const saveResult = await saveResponse.json()

      if (!saveResponse.ok || !saveResult.success) {
        toast.error('Failed to save credentials')
        setState((prev) => ({ ...prev, isLoading: false }))
        return
      }

      // Connection successful and credentials saved
      const integrationId = `${appSlug}_${Date.now()}`

      // Track integration connection (only for new connections, not edits)
      if (!editMode) {
        // Check if this is the first integration (activation)
        const isFirstIntegration =
          !connectedIntegrations?.providerIds || connectedIntegrations.providerIds.length === 0

        analytics.copilot.integrationConnected(appSlug, source)

        // Track activation if this is the first integration
        if (isFirstIntegration && onboardingData?.onboardingCompleted) {
          // Calculate time since onboarding completion
          const onboardingCompletedAt = onboardingData.updatedAt
            ? new Date(onboardingData.updatedAt).getTime()
            : null
          const timeSinceOnboarding = onboardingCompletedAt
            ? Math.round((Date.now() - onboardingCompletedAt) / 1000)
            : 0

          analytics.activation.firstIntegrationConnected(appSlug, source, timeSinceOnboarding)
        }
      }

      toast.success(
        editMode
          ? `Successfully updated ${config.name} credentials!`
          : `Successfully signed in to ${config.name}!`
      )
      onSuccess(integrationId, credentials)
      setState((prev) => ({ ...prev, credentials: {} }))
      onClose()
    } catch (error) {
      console.error('Error connecting integration:', error)
      toast.error('Failed to connect integration')
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [
    appSlug,
    config.name,
    credentials,
    isFramer,
    isSelfHosted,
    editMode,
    onClose,
    onSuccess,
    currentBusinessProfileId,
    connectedIntegrations,
    onboardingData,
    source,
  ])

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, credentials: {}, showSeoModal: false }))
    onClose()
  }, [onClose])

  const handleSeoComplete = useCallback(
    async (enabledSeo: boolean) => {
      setState((prev) => ({ ...prev, showSeoModal: false, isLoading: true }))

      try {
        // Save credentials with seoAutomationEnabled flag
        const credentialsToSave = {
          ...credentials,
          seoAutomationEnabled: enabledSeo,
        }

        const saveUrl = new URL(
          `/v2/integrations/credentials/${appSlug}`,
          process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI
        )
        if (currentBusinessProfileId) {
          saveUrl.searchParams.set('businessProfileId', currentBusinessProfileId)
        }

        const saveResponse = await fetch(saveUrl.toString(), {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(credentialsToSave),
        })

        const saveResult = await saveResponse.json()

        if (!saveResponse.ok || !saveResult.success) {
          toast.error('Failed to save credentials')
          setState((prev) => ({ ...prev, isLoading: false }))
          return
        }

        const integrationId = `${appSlug}_${Date.now()}`

        toast.success(
          enabledSeo
            ? 'Successfully connected with SEO automation enabled!'
            : 'Successfully connected to WordPress!'
        )
        // Convert boolean to string for callback signature
        onSuccess(integrationId, {
          ...credentialsToSave,
          seoAutomationEnabled: String(enabledSeo),
        })
        setState((prev) => ({ ...prev, credentials: {}, isLoading: false }))
        onClose()
      } catch (error) {
        console.error('Error saving credentials:', error)
        toast.error('Failed to save credentials')
        setState((prev) => ({ ...prev, isLoading: false }))
      }
    },
    [appSlug, credentials, onSuccess, onClose, currentBusinessProfileId]
  )

  const handleSeoSkip = useCallback(() => {
    // User skipped SEO setup, save credentials without SEO automation
    handleSeoComplete(false)
  }, [handleSeoComplete])

  const handleWordPressOAuth = useCallback(async () => {
    const siteUrl = credentials['siteUrl']
    if (!siteUrl?.trim()) {
      toast.error('Please enter your WordPress.com site URL')
      return
    }

    setState((prev) => ({ ...prev, isOAuthInProgress: true }))
    const success = await connectWordPressOAuthIntegration(siteUrl)
    setState((prev) => ({ ...prev, isOAuthInProgress: false }))

    if (!success) {
      toast.error('Failed to connect to WordPress.com')
      return
    }

    toast.success('Successfully connected to WordPress.com!')
    onSuccess(`wordpress_${Date.now()}`, { siteUrl, ...credentials })
    setState((prev) => ({ ...prev, credentials: {} }))
    onClose()
  }, [credentials, onSuccess, onClose])

  const handleShopifyOAuth = useCallback(async () => {
    const shopDomain = credentials['shopDomain']
    const clientId = credentials['clientId']
    const clientSecret = credentials['clientSecret']

    if (!shopDomain?.trim()) {
      toast.error('Please enter your Shopify shop domain')
      return
    }

    if (!clientId?.trim() || !clientSecret?.trim()) {
      toast.error('Please enter your custom app API key and secret')
      return
    }

    setState((prev) => ({ ...prev, isOAuthInProgress: true }))
    const result = await initiateShopifyOAuth({
      shopDomain,
      clientId,
      clientSecret,
    })
    setState((prev) => ({ ...prev, isOAuthInProgress: false }))

    if (!result.success) {
      toast.error(result.error || 'Failed to connect to Shopify')
      return
    }

    toast.success('Successfully connected to Shopify!')
    onSuccess(`shopify_${Date.now()}`, { shopDomain, ...credentials })
    setState((prev) => ({ ...prev, credentials: {} }))
    onClose()
  }, [credentials, onSuccess, onClose])

  const allFieldsFilled = useMemo(() => {
    if (isFramer) return credentials['connectionUrl']?.trim()
    if (isShopify) return credentials['shopDomain']?.trim()
    if (isSelfHosted) {
      return config.fields
        .filter((field) => field.type !== 'checkbox')
        .every((field) => credentials[field.key]?.trim())
    }
    return credentials['siteUrl']?.trim()
  }, [isFramer, isShopify, isSelfHosted, credentials, config.fields])

  return (
    <>
      <Modal isOpen={isOpen && !showSeoModal} onClose={handleClose}>
        <div className="font-hanken-grotesk flex w-full max-w-xl flex-col p-8">
          <div className="w-full rounded-3xl bg-white p-8 text-black shadow-xl">
            <h2 className="mb-2 text-2xl font-semibold text-black">
              {editMode ? `Edit ${config.name}` : `Connect to ${config.name}`}
            </h2>
            <p className="mb-6 text-base text-black/70">
              {editMode
                ? `Update your ${config.name} credentials`
                : `Enter your ${config.name} credentials below`}
            </p>

            {isLoadingCredentials ? (
              <div className="flex min-h-[200px] items-center justify-center">
                <Spinner size={32} />
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {isShopify ? (
                    <ShopifyCredentialsForm
                      credentials={credentials}
                      isOAuthInProgress={isOAuthInProgress}
                      onFieldChange={handleFieldChange}
                      onShopifyOAuth={handleShopifyOAuth}
                    />
                  ) : !isFramer ? (
                    <WordPressCredentialsForm
                      credentials={credentials}
                      isLoading={isLoading}
                      isTesting={isTesting}
                      isOAuthInProgress={isOAuthInProgress}
                      isSelfHosted={isSelfHosted}
                      allFieldsFilled={allFieldsFilled}
                      editMode={editMode}
                      onFieldChange={handleFieldChange}
                      onTest={handleTest}
                      onConnect={handleConnect}
                      onWordPressOAuth={handleWordPressOAuth}
                    />
                  ) : (
                    /* Framer form - keeping inline for now */
                    <>
                      <div className="rounded-2xl bg-blue-50 p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100">
                            <svg
                              className="h-4 w-4 text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h3 className="mb-2 text-sm font-semibold text-gray-900">
                              How to get your Connection URL
                            </h3>
                            <ol className="space-y-1.5 text-sm text-gray-700">
                              <li className="flex items-start gap-2">
                                <span className="font-medium">1.</span>
                                <span>
                                  Install the{' '}
                                  <a
                                    href="https://www.framer.com/marketplace/plugins/mcp/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline hover:text-blue-700"
                                  >
                                    MCP plugin
                                  </a>{' '}
                                  from Framer Marketplace
                                </span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="font-medium">2.</span>
                                <span>Open the MCP plugin in your Framer desktop app</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="font-medium">3.</span>
                                <span>Copy the connection URL displayed in the plugin</span>
                              </li>
                            </ol>
                            <p className="mt-2 text-xs text-gray-600">
                              <strong>Important:</strong> Framer must remain open while using this
                              connection
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="connectionUrl"
                          className="mb-1 block text-sm font-medium text-black"
                        >
                          Connection URL
                        </label>
                        <div className="relative">
                          <input
                            id="connectionUrl"
                            type={showFramerUrl ? 'text' : 'password'}
                            value={credentials['connectionUrl'] || ''}
                            onChange={(e) => handleFieldChange('connectionUrl', e.target.value)}
                            placeholder="https://mcp.unframer.co/sse?id=...&secret=..."
                            className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 pr-12 text-base text-black placeholder:text-gray-400 focus:border-[#B0B0B0] focus:outline-none"
                            disabled={isLoading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowFramerUrl(!showFramerUrl)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            disabled={isLoading}
                            aria-label={showFramerUrl ? 'Hide URL' : 'Show URL'}
                          >
                            {showFramerUrl ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="mt-6 flex gap-3">
                        <Button
                          onClick={handleTest}
                          disabled={!allFieldsFilled || isLoading || isTesting}
                          variant="secondary"
                          size="default"
                          className="flex-1"
                        >
                          {isTesting ? (
                            <>
                              <Spinner size={16} />
                              <span className="ml-2">Testing...</span>
                            </>
                          ) : (
                            'Test Connection'
                          )}
                        </Button>

                        <Button
                          onClick={handleConnect}
                          disabled={!allFieldsFilled || isLoading || isTesting}
                          size="default"
                          className="flex-1"
                        >
                          {isLoading ? (
                            <>
                              <Spinner size={16} />
                              <span className="ml-2">
                                {editMode ? 'Updating...' : 'Connecting...'}
                              </span>
                            </>
                          ) : editMode ? (
                            'Update'
                          ) : (
                            'Connect'
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-4 flex justify-center">
                  <button
                    onClick={handleClose}
                    className="text-sm text-black/70 transition-colors hover:text-black"
                    disabled={isLoading || isTesting || isOAuthInProgress}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      {showSeoModal && (
        <WordPressSeoSetupModal
          credentials={{
            siteUrl: credentials['siteUrl'],
            username: credentials['username'],
            applicationPassword: credentials['applicationPassword'],
          }}
          onComplete={handleSeoComplete}
          onSkip={handleSeoSkip}
        />
      )}
    </>
  )
})
